import React from 'react';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { accountEditSchema, AccountEditFormData } from './accountEditSchema';
import { useAccountEdit } from './hooks/useAccountEdit';
import { useBreadcrumbLabel } from '../../../../../shared/hooks/useBreadcrumbLabel';

/**
 * Curated list of common IANA timezones for the select input.
 */
const COMMON_TIMEZONES = [
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'America/Argentina/Buenos_Aires',
  'America/Bogota',
  'America/Chicago',
  'America/Denver',
  'America/Lima',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/New_York',
  'America/Phoenix',
  'America/Santiago',
  'America/Sao_Paulo',
  'America/Toronto',
  'Asia/Bangkok',
  'Asia/Calcutta',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Jakarta',
  'Asia/Jerusalem',
  'Asia/Karachi',
  'Asia/Kuala_Lumpur',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Atlantic/Azores',
  'Australia/Melbourne',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Athens',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Dublin',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Kiev',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Moscow',
  'Europe/Paris',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Pacific/Auckland',
  'Pacific/Honolulu',
  'UTC',
];

/**
 * AccountEditPage
 * Allows an admin to update an account's name and timezone.
 * Route: /accounts/:accountId/edit
 */
export const AccountEditPage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const { account, loading, submitting, error, onSubmit, onCancel } = useAccountEdit(accountId);
  useBreadcrumbLabel(accountId, account?.name);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AccountEditFormData>({
    resolver: zodResolver(accountEditSchema),
    defaultValues: { name: '', timezone: '' },
    values: account ? { name: account.name, timezone: account.timezone } : undefined,
  });

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600 }}>
      <Typography
        component="h1"
        sx={{
          fontSize: '24px',
          fontWeight: 600,
          lineHeight: '32px',
          letterSpacing: '-0.01em',
          color: '#0b1c30',
          mb: 3,
        }}
      >
        Edit Account
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{
          border: '1px solid #E2E8F0',
          borderRadius: '12px',
          p: 3,
          bgcolor: '#ffffff',
        }}
      >
        <Stack spacing={3}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Account Name"
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />

          <Controller
            name="timezone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Timezone"
                fullWidth
                required
                error={!!errors.timezone}
                helperText={errors.timezone?.message}
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Stack direction="row" spacing={2}>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : undefined}
              sx={{ bgcolor: '#003ec7', '&:hover': { bgcolor: '#0031a0' }, boxShadow: 'none' }}
            >
              {submitting ? 'Saving...' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={onCancel}
              disabled={submitting}
              sx={{
                borderColor: '#E2E8F0',
                color: '#0b1c30',
                '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
